// src/http/browser/client.ts
import type { ZodType } from "zod";
import type { Url } from "../../data-model/primitives.js";
import { unsafeBrandUrl } from "../../data-model/primitives.js";
import type { HttpClient, HttpRequestOptions, HttpResponse } from "../types.js";
import type {
  BrowserClient,
  BrowserPage,
  NavigateOptions,
  RuntimeContext,
  RuntimePage,
  RuntimeResponse,
  WaitForResponseJsonOpts,
  WaitForResponseTextOpts,
} from "./types.js";
import type { BrowserPool } from "./pool.js";
import { safeClosePage } from "./safe-close.js";
import { ChallengeUnresolvedError, CancelledError } from "../../core/errors.js";
import { isCfCookie } from "../cookie-jar.js";
import { CF_TITLE_PHRASES } from "../cf-challenge.js";

type WaitOpts =
  | {
      readonly timeoutMs?: number;
      readonly signal?: AbortSignal;
      readonly parse: "text";
      readonly skipBuffer?: boolean;
    }
  | {
      readonly timeoutMs?: number;
      readonly signal?: AbortSignal;
      readonly parse: "json";
      readonly schema: ZodType<unknown>;
      readonly skipBuffer?: boolean;
    };

interface BufferedResponse {
  url: string;
  status: number;
  headers: Record<string, string>;
  raw: RuntimeResponse;
}

// Cap how many responses we retain between `navigate()` and `waitForResponse()`.
// Each entry holds a strong ref to a Playwright Response (with its body buffer),
// so an uncapped buffer leaks memory for the page's lifetime. 20 is ample for the
// fast-path case (target XHR fired during goto/before listener attach).
const RESPONSE_BUFFER_LIMIT = 20;

const CHALLENGE_TITLE = new RegExp(`^(?:${CF_TITLE_PHRASES})`, "i");

export class BrowserHttpClient implements HttpClient {
  readonly #pool: BrowserPool;
  readonly #navTimeoutMs: number;

  constructor(opts: { pool: BrowserPool; navigationTimeoutMs?: number }) {
    this.#pool = opts.pool;
    this.#navTimeoutMs = opts.navigationTimeoutMs ?? 30_000;
  }

  async get(url: Url, opts?: HttpRequestOptions): Promise<HttpResponse> {
    const handle = await this.#pool.acquireContext();
    try {
      const page = await handle.context.newPage();
      try {
        const response = await page.goto(String(url), {
          timeout: this.#navTimeoutMs,
          waitUntil: "domcontentloaded",
          ...(opts?.signal ? { signal: opts.signal } : {}),
        });
        const title = await page.title();
        if (CHALLENGE_TITLE.test(title)) {
          throw new ChallengeUnresolvedError(url, "manual-solve");
        }
        const body = await page.content();
        const status = response?.status() ?? 0;
        const headers = response?.headers() ?? {};
        return { status, headers, body, url };
      } finally {
        await safeClosePage(page);
      }
    } catch (err) {
      if (opts?.signal?.aborted) {
        throw new CancelledError({ cause: err ?? opts.signal.reason });
      }
      throw err;
    } finally {
      await handle.release();
    }
  }
}

export class BrowserClientImpl implements BrowserClient {
  readonly #context: RuntimeContext;
  readonly #navTimeoutMs: number;

  constructor(opts: { context: RuntimeContext; navigationTimeoutMs?: number }) {
    this.#context = opts.context;
    this.#navTimeoutMs = opts.navigationTimeoutMs ?? 30_000;
  }

  async navigate(
    url: Url,
    signal: AbortSignal,
    opts: NavigateOptions = {},
  ): Promise<BrowserPage> {
    const page: RuntimePage = await this.#context.newPage();
    const buffer: BufferedResponse[] = [];
    const onResponse = (r: RuntimeResponse): void => {
      buffer.push({ url: r.url(), status: r.status(), headers: r.headers(), raw: r });
      if (buffer.length > RESPONSE_BUFFER_LIMIT) buffer.shift();
    };
    page.on("response", onResponse);

    if (opts.initScript !== undefined) {
      try {
        await page.addInitScript(opts.initScript);
      } catch (err) {
        page.off("response", onResponse);
        await safeClosePage(page);
        throw err;
      }
    }

    let navError: unknown;
    try {
      await page.goto(String(url), {
        timeout: this.#navTimeoutMs,
        waitUntil: "domcontentloaded",
        ...(signal ? { signal } : {}),
      });
    } catch (err) {
      navError = err;
    }

    function waitForResponse(
      pattern: RegExp,
      waitOpts: WaitForResponseTextOpts,
    ): Promise<{ url: Url; status: number; body: string }>;
    function waitForResponse<T>(
      pattern: RegExp,
      waitOpts: WaitForResponseJsonOpts<T>,
    ): Promise<{ url: Url; status: number; body: T }>;
    async function waitForResponse(
      pattern: RegExp,
      waitOpts: WaitOpts,
    ): Promise<{ url: Url; status: number; body: unknown }> {
      if (navError !== undefined) throw navError;
      const timeoutMs = waitOpts.timeoutMs ?? 30_000;
      const innerSignal = waitOpts.signal;
      const validate = async (raw: RuntimeResponse): Promise<unknown> => {
        if (waitOpts.parse === "text") return raw.text();
        const json = await raw.json();
        return waitOpts.schema.parse(json);
      };

      const existing = waitOpts.skipBuffer
        ? undefined
        : buffer.find((r) => pattern.test(r.url));
      if (existing) {
        return {
          // Playwright Response.url() always returns an absolute URL
          url: unsafeBrandUrl(existing.url),
          status: existing.status,
          body: await validate(existing.raw),
        };
      }

      return new Promise<{ url: Url; status: number; body: unknown }>((resolve, reject) => {
        function listener(r: RuntimeResponse): void {
          if (!pattern.test(r.url())) return;
          cleanup();
          validate(r)
            .then((body) =>
              // Playwright Response.url() always returns an absolute URL
              resolve({ url: unsafeBrandUrl(r.url()), status: r.status(), body }),
            )
            .catch(reject);
        }

        function onAbort(): void {
          cleanup();
          reject(new CancelledError({ cause: innerSignal?.reason ?? signal.reason }));
        }

        function cleanup(): void {
          clearTimeout(timer);
          page.off("response", listener);
          innerSignal?.removeEventListener("abort", onAbort);
          signal.removeEventListener("abort", onAbort);
        }

        const timer = setTimeout(() => {
          cleanup();
          reject(new Error(`Timeout waiting for response matching ${pattern}`));
        }, timeoutMs);

        if (innerSignal?.aborted) { onAbort(); return; }
        if (signal.aborted) { onAbort(); return; }
        innerSignal?.addEventListener("abort", onAbort, { once: true });
        signal.addEventListener("abort", onAbort, { once: true });
        page.on("response", listener);
      });
    }

    return {
      waitForResponse,

      async content(): Promise<string> {
        if (navError !== undefined) throw navError;
        return page.content();
      },

      async evaluate(script: string): Promise<unknown> {
        if (navError !== undefined) throw navError;
        return page.evaluate(script);
      },

      async close(): Promise<void> {
        page.off("response", onResponse);
        await safeClosePage(page);
      },
    };
  }
}

/** Snapshot a context's CF cookies for jar replay. */
export async function captureCfCookies(
  context: RuntimeContext,
  origin: string,
): Promise<{ cookies: ReadonlyArray<{ name: string; value: string }>; ua: string | null }> {
  const all = await context.cookies([origin]);
  const cookies = all
    .filter((c) => isCfCookie(c.name))
    .map((c) => ({ name: c.name, value: c.value }));
  // UA is set per-context at newContext({userAgent}) time; the pool callsite
  // owns this. captureCfCookies returns ua: null and the orchestrator pins the UA elsewhere.
  return { cookies, ua: null };
}
